import React, { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { accountingApiEvents, accountingApiSelectors } from '../../../api/accounting'
import { pathOr } from 'ramda'
import ViewStatementProvider from '../ViewStatement/ViewStatementProvider'
import ViewStatement from '../ViewStatement/ViewStatement'
import { uiEvents } from '../../../ui'

type StatementFormat = 'json' | 'pdf' | 'csv'

const STATEMENT_EVENTS = {
  json: accountingApiEvents.depositBankStatementJson_request,
  pdf: accountingApiEvents.depositBankStatementPdf_request,
  csv: accountingApiEvents.depositBankStatementCsv_request,
}

const DepositBankStatement = (): React.ReactElement => {
  const dispatch = useDispatch()

  const fetchDepositBankStatement = useCallback(
    (startDate: string, endDate: string, format: StatementFormat = 'json') => {
      dispatch(
        STATEMENT_EVENTS[format]({
          start_date: startDate,
          end_date: endDate,
        }),
      )
    },
    [dispatch],
  )

  const onClose = useCallback(() => dispatch(uiEvents.redirect('/dashboard')), [dispatch])

  const fetchDepositBankStatementPdf = useCallback(
    (startDate: string, endDate: string) => fetchDepositBankStatement(startDate, endDate, 'pdf'),
    [fetchDepositBankStatement],
  )

  const fetchDepositBankStatementCsv = useCallback(
    (startDate: string, endDate: string) => fetchDepositBankStatement(startDate, endDate, 'csv'),
    [fetchDepositBankStatement],
  )

  const statementData = useSelector(accountingApiSelectors.getDepositBankStatementJson)
  const statementPdf = useSelector(accountingApiSelectors.getDepositBankStatementPdf)
  const statementCsv = useSelector(accountingApiSelectors.getDepositBankStatementCsv)
  const fileName = `${pathOr('', ['context', 'agency', 'name'], statementData) as string} Deposit Bank Statement`

  const handleParamChange = useCallback(
    (startDate: string, endDate: string) => {
      fetchDepositBankStatement(startDate, endDate)
      fetchDepositBankStatementPdf(startDate, endDate)
    },
    [fetchDepositBankStatement, fetchDepositBankStatementPdf],
  )

  return useMemo(() => {
    return (
      <ViewStatementProvider
        isOpen
        onClose={onClose}
        onParamChange={handleParamChange}
        statementData={statementData}
        downloadStatement={fetchDepositBankStatementPdf}
        statementPdf={statementPdf}
        pdfFileName={fileName}
        downloadStatementCsv={fetchDepositBankStatementCsv}
        statementCsv={statementCsv}
        csvFileName={fileName}
      >
        <ViewStatement title="Deposit Bank Statement" />
      </ViewStatementProvider>
    )
  }, [
    onClose,
    fetchDepositBankStatementPdf,
    fetchDepositBankStatementCsv,
    statementData,
    statementPdf,
    statementCsv,
    fileName,
    handleParamChange,
  ])
}

export default DepositBankStatement
